import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { onConsentGranted } from '@/lib/metaPixel';

const STORAGE_KEY = 'propveiw_cookie_consent_v1';

export const CookieBanner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Slight delay so the page can render first
        const t = setTimeout(() => setShow(true), 800);
        return () => clearTimeout(t);
      }
    } catch (err) {
      // localStorage blocked - still show once per session
      console.warn('CookieBanner: unable to read consent from storage:', err);
      setShow(true);
    }
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        choice,
        at: new Date().toISOString(),
      }));
    } catch (err) {
      // localStorage blocked (private mode / disabled cookies) — user's
      // choice won't persist but the current dismissal is fine.
      console.warn('CookieBanner: unable to persist consent:', err);
    }
    setShow(false);
    if (choice === 'all') {
      // Boot Meta Pixel + any other consent-gated trackers now that the
      // visitor has opted in.
      onConsentGranted();
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-2xl z-[100] animate-fade-in"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="bg-[#0c1530] text-slate-100 border border-[#2a4a7a] shadow-2xl shadow-black/40 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 ring-1 ring-amber-300/10">
        <div className="w-9 h-9 rounded-lg bg-amber-400/15 text-amber-300 ring-1 ring-amber-400/30 flex items-center justify-center flex-shrink-0">
          <Cookie className="h-4.5 w-4.5" />
        </div>
        <div className="flex-1 text-sm leading-relaxed">
          <p className="font-medium text-white">We use cookies to keep PROP VEIW running smoothly.</p>
          <p className="text-slate-300 mt-1">
            Essential cookies keep you logged in; analytics cookies help us learn what to improve.
            See our{' '}
            <Link to="/cookies" className="underline text-amber-300 hover:text-amber-200">
              Cookie Notice
            </Link>{' '}
            for details.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => persist('essential_only')}
            className="text-slate-300 hover:bg-[#13234a] hover:text-white"
            data-testid="cookie-essential-btn"
          >
            Essential only
          </Button>
          <Button
            size="sm"
            onClick={() => persist('all')}
            className="bg-amber-400 hover:bg-amber-300 text-[#070d1c] font-semibold shadow-md shadow-amber-500/20"
            data-testid="cookie-accept-btn"
          >
            Accept all
          </Button>
          <button
            onClick={() => persist('dismissed')}
            className="p-1 rounded text-slate-500 hover:text-slate-300 hover:bg-[#13234a] transition-colors hidden sm:block"
            aria-label="Dismiss"
            data-testid="cookie-dismiss-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
